import { decodeBase64Url, encodeBase64Url } from './walletPasskeyCrypto'

type CredentialDescriptorJSON = Omit<PublicKeyCredentialDescriptor, 'id'> & { id: string }
export type PasskeyCreationOptionsJSON = Omit<
    PublicKeyCredentialCreationOptions,
    'challenge' | 'user' | 'excludeCredentials' | 'extensions'
> & {
    challenge: string
    user: Omit<PublicKeyCredentialUserEntity, 'id'> & { id: string }
    excludeCredentials?: CredentialDescriptorJSON[]
}
export type PasskeyRequestOptionsJSON = Omit<
    PublicKeyCredentialRequestOptions,
    'challenge' | 'allowCredentials' | 'extensions'
> & {
    challenge: string
    allowCredentials?: CredentialDescriptorJSON[]
    extensions?: { prf?: { eval?: { first?: string } } }
}

export function passkeyBrowserUnavailableReason(): string | null {
    if (typeof window === 'undefined') return 'Open Treechat in a supported browser.'
    // App builds whose WebView is associated with this site for passkeys advertise it.
    if (window.ReactNativeWebView && !window.KNOVIGATOR_NATIVE_CAPS?.webAuthnPasskeys) {
        return 'To use passkey recovery, update the Treechat app or open app.treechat.com in Safari or Chrome.'
    }
    if (
        !window.isSecureContext ||
        !window.PublicKeyCredential ||
        !navigator.credentials?.create ||
        !navigator.credentials?.get ||
        !globalThis.crypto?.subtle
    ) {
        return 'Passkey recovery is unavailable in this browser. Your wallet backup and device transfer remain available.'
    }
    return null
}

function requirePasskeyBrowser() {
    const reason = passkeyBrowserUnavailableReason()
    if (reason) throw new Error(reason)
}

// Called directly by a click handler, before any network or storage await. Safari
// may require a fresh user gesture for each WebAuthn operation.
export async function createWalletPasskey(options: PasskeyCreationOptionsJSON) {
    requirePasskeyBrowser()
    const credential = (await navigator.credentials.create({
        publicKey: {
            ...options,
            challenge: decodeBase64Url(options.challenge),
            user: { ...options.user, id: decodeBase64Url(options.user.id) },
            excludeCredentials: options.excludeCredentials?.map(item => ({
                ...item,
                id: decodeBase64Url(item.id),
            })),
            extensions: { prf: {} } as AuthenticationExtensionsClientInputs,
        },
    })) as PublicKeyCredential | null
    if (!credential) throw new Error('Passkey creation was cancelled.')
    const extensions =
        credential.getClientExtensionResults() as AuthenticationExtensionsClientOutputs & {
            prf?: { enabled?: boolean }
        }
    if (extensions.prf?.enabled !== true) {
        throw new Error(
            'This passkey provider does not support wallet recovery. No recovery backup was enabled. Try another provider or use your wallet backup.',
        )
    }
    return serializeWalletPasskeyCredential(credential)
}

export async function useWalletPasskey(options: PasskeyRequestOptionsJSON) {
    requirePasskeyBrowser()
    const salt = options.extensions?.prf?.eval?.first
    if (!salt || decodeBase64Url(salt).byteLength !== 32) {
        throw new Error('Invalid passkey recovery request. Start again.')
    }
    const credential = (await navigator.credentials.get({
        publicKey: {
            ...options,
            challenge: decodeBase64Url(options.challenge),
            allowCredentials: options.allowCredentials?.map(item => ({
                ...item,
                id: decodeBase64Url(item.id),
            })),
            userVerification: 'required',
            extensions: {
                prf: { eval: { first: decodeBase64Url(salt) } },
            } as AuthenticationExtensionsClientInputs,
        },
    })) as PublicKeyCredential | null
    if (!credential) throw new Error('Passkey recovery was cancelled.')
    const extensions =
        credential.getClientExtensionResults() as AuthenticationExtensionsClientOutputs & {
            prf?: { results?: { first?: ArrayBuffer } }
        }
    const result = extensions.prf?.results?.first
    if (!result || result.byteLength !== 32) {
        if (credential.authenticatorAttachment === 'cross-platform') {
            throw new Error(
                "This passkey is on another device and can't unlock wallet backups from here. Use a passkey saved on this device, or recover on the device that holds it.",
            )
        }
        throw new Error(
            'This passkey cannot unlock wallet backups in this browser. Try the browser and provider you used to set up recovery.',
        )
    }
    return {
        credential: serializeWalletPasskeyCredential(credential),
        prfOutput: new Uint8Array(result),
    }
}

// Never call credential.toJSON(): browsers can include the secret PRF result in
// it. Explicitly allowlist only public WebAuthn verification fields.
export function serializeWalletPasskeyCredential(credential: PublicKeyCredential) {
    if (credential.type !== 'public-key') throw new Error('Invalid passkey credential.')
    const response = credential.response
    const common = { clientDataJSON: encodeBase64Url(new Uint8Array(response.clientDataJSON)) }
    let serialized: Record<string, unknown>
    if ('attestationObject' in response) {
        const registration = response as AuthenticatorAttestationResponse
        serialized = {
            ...common,
            attestationObject: encodeBase64Url(new Uint8Array(registration.attestationObject)),
            transports: registration.getTransports?.() || [],
        }
    } else {
        const assertion = response as AuthenticatorAssertionResponse
        serialized = {
            ...common,
            authenticatorData: encodeBase64Url(new Uint8Array(assertion.authenticatorData)),
            signature: encodeBase64Url(new Uint8Array(assertion.signature)),
            userHandle: assertion.userHandle
                ? encodeBase64Url(new Uint8Array(assertion.userHandle))
                : null,
        }
    }
    return {
        id: credential.id,
        rawId: encodeBase64Url(new Uint8Array(credential.rawId)),
        type: credential.type,
        response: serialized,
        clientExtensionResults: {},
    }
}

export function walletPasskeyErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'AbortError') {
            return 'The passkey request was cancelled or timed out. Your wallet has not changed.'
        }
        if (error.name === 'InvalidStateError') {
            return 'This passkey is already registered. Use its existing recovery entry below.'
        }
        if (error.name === 'SecurityError' || error.name === 'NotSupportedError') {
            return 'Passkey recovery is unavailable here. Open app.treechat.com in a supported browser.'
        }
        return error.message
    }
    return 'Passkey recovery could not finish. Please try again.'
}
